/**
 * Ensures the provided URL or path starts with the current host.
 *
 * If the input is already an absolute URL (including protocol and host), it is returned as is.
 * If the input is a relative path, the current window location's origin is prepended.
 *
 * @param {string} to - The URL or path to evaluate.
 * @returns {string} The absolute URL starting with the current host.
 */
export function getAbsoluteUrl(to: string): string {
  try {
    new URL(to);
    return to;
  } catch {
    return `${window.location.origin}${to.startsWith('/') ? '' : '/'}${to}`;
  }
}

/**
 * Appends app-specific query params (show-cookie-consent, user-id) from current location to a given path+query.
 *
 * @param {string} pathWithQuery - The base path with query string (e.g. '/foo?bar=1').
 * @returns {string} The resulting path with merged app query params if present in current URL.
 */
export function keepAppQuery(pathWithQuery: string) {
  // Get current URL search params
  const currentParams = new URLSearchParams(window.location.search);
  const appKeys = ['show-cookie-consent', 'user-id'];
  const appParams = new URLSearchParams();
  appKeys.forEach(key => {
    if (currentParams.has(key)) {
      appParams.set(key, currentParams.get(key)!);
    }
  });

  // Extract path and query from the input
  const [path, query1] = pathWithQuery.split('?');
  const params = new URLSearchParams(query1 || '');

  // Add/override with appParams
  for (const [key, value] of appParams.entries()) {
    params.set(key, value);
  }
  const mergedQuery = params.toString();
  return mergedQuery ? `${path}?${mergedQuery}` : path;
}
